import { Rng } from '../core/rng';
import { clamp, Vec3, wrapAngle } from '../core/math';
import {
  CAPSTAN, DECK, HATCHES, LADDERS, MASTS, WHEEL, cannonMount, deckHalfWidth, deckY, mast, mastXAt, sheerY, type CannonSpec, type MastId,
} from '../data/ship';
import type { ShipClass } from '../data/classes';
import type { DamageModel, MastDamage } from './damage';
import type { FireSystem } from './fire';
import type { SimEvents } from './events';
import type { Ship } from './ship';
import type { SailState } from './sails';

// The ship's company. A slice of the crew is simulated as individuals on
// deck (the rest are below, feeding up replacements). An officer loop turns
// the ship's needs into tasks; men pick them up imperfectly (hesitation,
// the odd wrong choice), walk the decks and ladders, climb the shrouds, and
// their presence is what makes guns reload, the helm answer, sails get set,
// pumps run and holes get plugged. They flinch at near misses, are shaken by
// losses, drag the wounded below, and in the end may strike or abandon ship.

export type TaskKind =
  | 'idle' | 'helm' | 'gun' | 'powder' | 'sail' | 'pump' | 'repair' | 'fire' | 'rig' | 'patch' | 'spar'
  | 'lookout' | 'aid' | 'musket' | 'cover' | 'board' | 'abandon' | 'surrender';

/**
 * The damage parties a captain can order to work (every man in one is a man
 * off the guns): the pumps and carpenters at the leaks, the fire parties,
 * the sailmakers at the canvas, and the riggers with the spare spars.
 */
export type Party = 'leaks' | 'fire' | 'canvas' | 'spars';
export const PARTIES: readonly Party[] = ['leaks', 'fire', 'canvas', 'spars'];
/** Which parties have been ordered to it (the rest are left to the officers). */
export type Orders = Record<Party, boolean>;

/** Men's work (one man, seconds) to send up a spare spar, by the lostLevel being made good. */
const SPAR_WORK = [0, 220, 420, 900];

export type Stance = 'stand' | 'walk' | 'run' | 'work' | 'haul' | 'crouch' | 'aim' | 'down' | 'dead' | 'swim' | 'climb' | 'fight' | 'hands';

export interface CrewMember {
  id: number;
  role: 'officer' | 'gunner' | 'seaman' | 'carpenter' | 'marine' | 'boy';
  /** Design-space deck position. */
  x: number;
  z: number;
  /** Height above the deck under them (climbing, aloft), design m. */
  alt: number;
  onBoard: boolean;
  /** World position once in the water. */
  wx: number;
  wy: number;
  wz: number;
  facing: number;
  task: TaskKind;
  ref: number;
  tx: number;
  tz: number;
  tAlt: number;
  /** Waypoint (ladders, shrouds) before the target, if any. */
  wpx: number;
  wpz: number;
  hasWp: boolean;
  health: number;
  alive: boolean;
  experience: number;
  courage: number;
  hesitate: number;
  flinch: number;
  stance: Stance;
  anim: number;
  /** 0..1 how much useful work he did this step. */
  work: number;
  /** Seconds spent on the current task. */
  taskTime: number;
  appearance: number;
}

export interface GunCrewSlot {
  spec: CannonSpec;
  destroyed: boolean;
  /** Written by the crew each step: men working the gun. */
  crew: number;
}

export interface CrewContext {
  ship: Ship;
  damage: DamageModel;
  fire: FireSystem;
  guns: GunCrewSlot[];
  /** Beat to quarters. */
  battle: boolean;
  /** Our fighting power over the enemy's, as the men see it (1 even). */
  odds?: number;
  /** Side the enemy is on (+1 starboard, -1 port, 0 none). */
  threatSide: number;
  /** Range to the nearest enemy, m. */
  threatRange: number;
  /** Guns currently being served on this side (+1/-1), from the captain. */
  gunSide: number;
  abandon: boolean;
  surrender: boolean;
  boarding: number;
  /** All hands to the boarding (every man fit to fight goes to the rail). */
  allHands?: boolean;
  /** Damage parties ordered to it over the guns. */
  orders?: Orders;
  /** Dutch courage: grog in them, 0..1 (fades). */
  grog?: number;
  events: SimEvents;
  vessel: number;
  waterHeight: (x: number, z: number) => number;
}

interface Need {
  kind: TaskKind;
  ref: number;
  slots: number;
  priority: number;
  x: number;
  z: number;
  alt: number;
  /** Spread offsets for multiple men. */
  spread: number;
  aloftMast?: MastId;
}

type Level = 'quarter' | 'main' | 'fore';
function levelOf(x: number): Level {
  return x < DECK.quarterFrontX ? 'quarter' : x > DECK.foreBackX ? 'fore' : 'main';
}

/** Height of the walking surface at design (x, z), ladders included. */
export function walkHeight(x: number, z: number): number {
  for (const l of LADDERS) {
    if (Math.abs(z - l.low.z) > 0.45) continue;
    const a = Math.min(l.low.x, l.high.x), b = Math.max(l.low.x, l.high.x);
    if (x < a || x > b) continue;
    const t = (x - l.low.x) / (l.high.x - l.low.x);
    return deckY(l.low.x) + (deckY(l.high.x) - deckY(l.low.x)) * clamp(t, 0, 1);
  }
  return deckY(x);
}

const OBSTACLES: { x: number; z: number; r: number }[] = [
  ...MASTS.map((m) => ({ x: m.x, z: 0, r: m.radius + 0.7 })),
  ...HATCHES.flatMap((h) => [-0.6, 0, 0.6].map((f) => ({ x: h.x + f * h.hx, z: 0, r: h.hz + 0.2 }))),
  { x: CAPSTAN.x, z: 0, r: CAPSTAN.r + 0.35 },
  { x: WHEEL.x - 0.25, z: 0, r: 0.8 },
  { x: WHEEL.binnacleX, z: 0, r: 0.45 },
];

export class Crew {
  readonly members: CrewMember[] = [];
  /** Men below (fresh hands and replacements). */
  reserve: number;
  /** Men carried below wounded. */
  woundedBelow = 0;
  killed = 0;
  morale: number;
  readonly complement: number;
  // Outputs for the ship, refreshed every step.
  helmCrew = 0;
  sailHands = 0;
  pumpers = 0;
  lookout = false;
  marines = 0;
  /** Mean experience of the men serving guns. */
  gunnery = 0.5;
  /** Men on deck fit for a boarding fight. */
  fighters = 0;
  /** Of them, the men at the rail in a boarding (and fit to fight). */
  boarders = 0;
  /**
   * How the fight's going, as the men feel it: our shot striking home raises
   * it, theirs lowers it, and it fades over a minute or so. It lifts or sinks
   * the heart they settle toward.
   */
  momentum = 0;
  /** Men told off to each party (and serving the guns), refreshed every step. */
  readonly party: Record<Party | 'guns', number> = { leaks: 0, fire: 0, canvas: 0, spars: 0, guns: 0 };
  private officerTimer = 0;
  private reserveTimer = 0;
  private readonly rng: Rng;
  private nextId = 0;
  private needs: Need[] = [];

  constructor(readonly cls: ShipClass, seed: number, skill: number) {
    this.rng = new Rng(seed);
    this.complement = cls.crew;
    this.reserve = cls.crew - cls.deckCrew;
    this.morale = 0.75 + this.rng.range(-0.1, 0.1);
    for (let i = 0; i < cls.deckCrew; i++) this.members.push(this.spawn(i === 0 ? 'officer' : i < 3 ? 'carpenter' : i % 7 === 0 ? 'marine' : i % 5 === 0 ? 'boy' : 'seaman', skill, true));
  }

  private spawn(role: CrewMember['role'], skill: number, initial: boolean): CrewMember {
    const r = this.rng;
    // Scatter across the waist, or up the main hatch for replacements.
    const x = initial ? r.range(-5, 11) : HATCHES[0]!.x;
    const hw = deckHalfWidth(x) - 0.6;
    const z = initial ? r.range(-hw, hw) : r.range(-0.8, 0.8);
    const exp = clamp(r.range(0.2, 0.95) + skill + (role === 'officer' ? 0.2 : 0), 0.05, 1);
    return {
      id: this.nextId++, role, x, z, alt: 0, onBoard: true, wx: 0, wy: 0, wz: 0, facing: r.range(-3, 3),
      task: 'idle', ref: -1, tx: x, tz: z, tAlt: 0, wpx: 0, wpz: 0, hasWp: false,
      health: 1, alive: true, experience: exp, courage: clamp(r.range(0.25, 1) + (role === 'marine' ? 0.15 : 0), 0, 1),
      hesitate: 0, flinch: 0, stance: 'stand', anim: r.range(0, 10), work: 0, taskTime: 0, appearance: r.next(),
    };
  }

  /** Men still able to work (on deck, fit) plus fresh hands below. */
  /** The morale her people's chances are worth, as they see them. */
  heart(ctx: CrewContext): number {
    const ship = ctx.ship;
    let h = 0.8;
    if (ctx.battle) {
      const odds = Math.max(0.05, ctx.odds ?? 1);
      h = 0.64 + 0.18 * clamp(Math.log2(odds) / 1.5, -1, 1);
    }
    const mobility = ship.sails.sailHealth * ship.rudderHealth;
    h -= 0.7 * Math.pow(1 - clamp(ctx.damage.integrity, 0, 1), 1.3);
    h -= 0.4 * clamp(ship.flooding * 1.6, 0, 1);
    h -= ctx.fire.worst > 0.5 ? 0.15 : 0;
    h -= 0.22 * (1 - clamp(mobility, 0, 1)) * (ctx.battle ? 1 : 0.3);
    h -= 0.3 * (1 - clamp(this.strength, 0, 1));
    if (ctx.battle) h += 0.16 * Math.tanh(this.momentum);
    h += 0.12 * (ctx.grog ?? 0);
    return clamp(h, 0.02, 0.95);
  }

  get effective(): number {
    let n = 0;
    for (const m of this.members) if (m.alive && m.onBoard && m.health > 0.4) n++;
    return n + this.reserve;
  }

  /** Fraction of the complement still fit. */
  get strength(): number {
    return this.effective / this.complement;
  }

  /** Splinters, grape or blast: casualties among men near a design point. */
  strike(x: number, y: number, z: number, radius: number, lethality: number, ctx: { ship: Ship; events: SimEvents; vessel: number } | null): number {
    let hurt = 0;
    for (const m of this.members) {
      if (!m.alive || !m.onBoard) continue;
      const my = walkHeight(m.x, m.z) + m.alt + 0.9;
      const d = Math.hypot(m.x - x, my - y, m.z - z);
      if (d > radius * 2.2) continue;
      // Near misses shake men up.
      m.flinch = Math.max(m.flinch, this.rng.range(0.5, 1.8) * (1.2 - m.courage));
      if (d > radius) continue;
      const p = lethality * (1 - d / radius) * (m.stance === 'crouch' ? 0.45 : 1);
      if (this.rng.chance(p)) {
        const dmg = this.rng.range(0.3, 1.3);
        this.injure(m, dmg, ctx);
        hurt++;
      }
    }
    return hurt;
  }

  /** Shot passing along a line (design space) - grape and ball through the waist. */
  sweep(ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius: number, lethality: number, ctx: { ship: Ship; events: SimEvents; vessel: number }): number {
    let hurt = 0;
    const dx = bx - ax, dy = by - ay, dz = bz - az;
    const L2 = dx * dx + dy * dy + dz * dz || 1;
    for (const m of this.members) {
      if (!m.alive || !m.onBoard) continue;
      const my = walkHeight(m.x, m.z) + m.alt + 0.9;
      const t = clamp(((m.x - ax) * dx + (my - ay) * dy + (m.z - az) * dz) / L2, 0, 1);
      const d = Math.hypot(ax + dx * t - m.x, ay + dy * t - my, az + dz * t - m.z);
      if (d < radius * 3) m.flinch = Math.max(m.flinch, this.rng.range(0.4, 1.5) * (1.2 - m.courage));
      if (d < radius && this.rng.chance(lethality * (m.stance === 'crouch' ? 0.4 : 1))) {
        this.injure(m, this.rng.range(0.5, 1.5), ctx);
        hurt++;
      }
    }
    return hurt;
  }

  injure(m: CrewMember, amount: number, ctx: { ship: Ship; events: SimEvents; vessel: number } | null): void {
    m.health -= amount;
    const killed = m.health <= 0;
    if (killed) {
      m.alive = false;
      m.stance = 'dead';
      m.work = 0;
      this.killed++;
    } else if (m.health < 0.4) {
      m.stance = 'down';
    }
    this.morale = clamp(this.morale - (killed ? 0.012 : 0.006) * (m.role === 'officer' ? 4 : 1) * (40 / Math.max(20, this.complement)) * 4, 0, 1);
    // Men nearby are shaken for a moment.
    for (const o of this.members) {
      if (o === m || !o.alive) continue;
      const d = Math.hypot(o.x - m.x, o.z - m.z);
      if (d < 4) o.hesitate = Math.max(o.hesitate, this.rng.range(0.3, 2) * (1.1 - o.courage));
    }
    if (ctx) {
      const p = ctx.ship.designToWorld(tmpV.set(m.x, walkHeight(m.x, m.z) + m.alt + 0.9, m.z), tmpV);
      ctx.events.push({ type: 'casualty', x: p.x, y: p.y, z: p.z, vessel: ctx.vessel, killed });
    }
  }

  /** Men aloft on a mast that has just gone by the board. */
  loseAloft(id: MastId, ctx: { ship: Ship; events: SimEvents; vessel: number }): void {
    const spec = mast(id);
    for (const m of this.members) {
      if (!m.alive || m.alt < 1.5 || Math.abs(m.x - spec.x) > 3) continue;
      this.injure(m, 2, ctx);
    }
  }

  update(dt: number, ctx: CrewContext): void {
    this.officerTimer -= dt;
    if (this.officerTimer <= 0) {
      this.officerTimer = 0.6;
      this.plan(ctx);
    }
    // Replacements come up from below as men fall.
    this.reserveTimer -= dt;
    const onDeck = this.members.filter((m) => m.alive && m.onBoard && m.health > 0.4).length;
    if (this.reserve > 0 && onDeck < this.cls.deckCrew && this.reserveTimer <= 0 && !ctx.abandon && !ctx.surrender) {
      this.reserveTimer = this.rng.range(3, 8);
      this.reserve--;
      const idx = this.members.findIndex((m) => !m.alive && m.onBoard && m.task === 'idle' && m.stance === 'dead' && m.taskTime > 30);
      const fresh = this.spawn(this.rng.chance(0.2) ? 'gunner' : 'seaman', 0, false);
      if (idx >= 0) this.members.splice(idx, 1);
      this.members.push(fresh);
    }

    // Morale settles, over a minute or two, toward what the men make of
    // their chances: even odds and a sound ship keep them in heart; being
    // outgunned, holed, flooding, on fire or crippled aloft takes it out of
    // them. (Shocks - men falling, masts going, a magazine - come on top.)
    this.morale = clamp(this.morale + (this.heart(ctx) - this.morale) * Math.min(1, dt / 70), 0, 1);
    this.momentum *= Math.exp(-dt / 45);
    const party = this.party;
    party.leaks = party.fire = party.canvas = party.spars = party.guns = 0;

    let helm = 0, sail = 0, pumps = 0, look = false, marines = 0, gunExp = 0, gunN = 0, fighters = 0, boarders = 0;
    for (const g of ctx.guns) g.crew = 0;
    for (const m of this.members) {
      m.anim += dt;
      m.taskTime += dt;
      if (!m.alive) continue;
      if (!m.onBoard) {
        this.swim(m, dt, ctx);
        continue;
      }
      if (m.health < 0.4) {
        m.stance = 'down';
        m.work = 0;
        continue;
      }
      fighters++;
      switch (m.task) {
        case 'board': boarders++; break;
        case 'pump': case 'repair': party.leaks++; break;
        case 'fire': party.fire++; break;
        case 'patch': party.canvas++; break;
        case 'rig': case 'spar': party.spars++; break;
        case 'gun': case 'powder': party.guns++; break;
        default: break;
      }
      m.flinch = Math.max(0, m.flinch - dt);
      m.hesitate = Math.max(0, m.hesitate - dt);
      this.move(m, dt, ctx);
      // Work only once in place, steady, and not ducking.
      const there = Math.hypot(m.x - m.tx, m.z - m.tz) < 0.45 && Math.abs(m.alt - m.tAlt) < 0.4;
      const eff = (0.55 + 0.45 * m.experience) * spirit(this.morale) * clamp(m.health, 0.4, 1);
      m.work = there && m.flinch <= 0 && m.hesitate <= 0 ? eff : 0;
      if (there && m.flinch <= 0) m.stance = workStance(m.task);
      if (m.flinch > 0 && m.stance !== 'climb') m.stance = 'crouch';
      if (m.work <= 0) continue;
      switch (m.task) {
        case 'helm': helm = Math.max(helm, m.work); break;
        case 'gun': {
          const g = ctx.guns[m.ref];
          if (g && !g.destroyed) {
            g.crew += m.work;
            gunExp += m.experience;
            gunN++;
          }
          break;
        }
        case 'sail': sail += m.work; break;
        case 'pump': pumps += m.work; break;
        case 'repair': {
          const h = ctx.damage.holes[m.ref];
          if (h && h.patched < 1) {
            // Shot plugs for small holes; big ones need planks, sheets of lead
            // and a sail fothered over the outside - slow, wet work.
            h.patched = Math.min(1, h.patched + (dt * m.work * (m.role === 'carpenter' ? 1 : 0.5) * 0.004) / (h.r * h.r * 20 + 0.05));
            if (h.patched >= 1) ctx.damage.version++;
          }
          break;
        }
        case 'fire': {
          const c = ctx.fire.cells[m.ref];
          if (c) c.fighters += m.work;
          break;
        }
        case 'rig': {
          const md = Object.values(ctx.damage.masts)[m.ref];
          if (md) md.rigging = Math.min(1, md.rigging + dt * m.work * 0.006);
          break;
        }
        case 'patch': {
          const sail = ctx.ship.sails.sails[m.ref];
          if (sail && !sail.lost) this.stitch(sail, dt * m.work, ctx);
          break;
        }
        case 'spar': {
          const md = Object.values(ctx.damage.masts)[m.ref];
          if (md && md.lostLevel > 0 && md.wreck === 0 && ctx.damage.spares > 0) {
            md.jury += (dt * m.work) / SPAR_WORK[md.lostLevel]!;
            if (md.jury >= 1) this.sendUp(md, ctx);
          }
          break;
        }
        case 'lookout': look = true; break;
        case 'musket': marines += m.work; break;
        case 'aid': {
          const w = this.members.find((o) => o.id === m.ref);
          if (w && w.alive && w.health < 0.4 && w.onBoard && Math.hypot(w.x - m.x, w.z - m.z) < 1) {
            if (m.taskTime > 4) {
              // Carried below to the surgeon.
              w.onBoard = false;
              w.alive = false;
              w.stance = 'dead';
              w.x = -1000;
              this.woundedBelow++;
              m.task = 'idle';
            }
          }
          break;
        }
        default: break;
      }
    }
    this.helmCrew = helm;
    this.sailHands = clamp(sail / 8, 0, 1);
    this.pumpers = pumps;
    this.lookout = look;
    this.marines = marines;
    this.gunnery = gunN > 0 ? gunExp / gunN : 0.5;
    this.fighters = fighters;
    this.boarders = boarders;
    // Keep the dead from piling up forever in the member list.
    if (this.members.length > this.cls.deckCrew * 3) {
      const i = this.members.findIndex((m) => !m.alive);
      if (i >= 0) this.members.splice(i, 1);
    }
  }

  /** The officer's view of what needs doing, turned into assignments. */
  private plan(ctx: CrewContext): void {
    const { ship, damage, fire } = ctx;
    const o = ctx.orders;
    const needs: Need[] = [];
    const r = this.rng;
    if (ctx.abandon || ctx.surrender) {
      for (const m of this.members) {
        if (!m.alive || !m.onBoard || m.health < 0.4) continue;
        if (ctx.surrender && m.task !== 'surrender') this.assign(m, { kind: 'surrender', ref: 0, slots: 1, priority: 0, x: r.range(-4, 8), z: r.range(-1.5, 1.5), alt: 0, spread: 0 });
        if (ctx.abandon && m.task !== 'abandon') {
          const side = ship.heel > 0 ? -1 : 1; // the high side
          const x = r.range(-6, 11);
          this.assign(m, { kind: 'abandon', ref: side, slots: 1, priority: 0, x, z: side * (deckHalfWidth(x) - 0.2), alt: 0, spread: 0 });
        }
      }
      return;
    }
    // Helm: someone must always be at the wheel.
    needs.push({ kind: 'helm', ref: 0, slots: 1, priority: 100, x: WHEEL.x - 0.75, z: 0, alt: 0, spread: 0 });
    // Fires, hottest and nearest the magazine first.
    fire.cells.forEach((c, i) => {
      if (c.heat < 0.015) return;
      const nearMag = Math.abs(c.x - damage.magazine.x) < 4 ? 20 : 0;
      needs.push({ kind: 'fire', ref: i, slots: Math.ceil(c.heat * (o?.fire ? 8 : 5)) + (o?.fire ? 1 : 0), priority: (o?.fire ? 97 : 78) + nearMag + c.heat * 10, x: c.x, z: c.z * (c.level === 0 ? 0.3 : 1), alt: 0, spread: 1.4 });
    });
    // Guns: full crews to the engaged side, a skeleton to the other.
    ctx.guns.forEach((g, i) => {
      if (g.destroyed) return;
      const onSide = ctx.gunSide === 0 || g.spec.side === ctx.gunSide;
      if (!ctx.battle) return;
      const m = cannonMount(g.spec);
      const need = g.spec.deck === 'main' ? 3 : 2;
      const back = g.spec.side * (g.spec.length * 0.55 + 0.55);
      needs.push({ kind: 'gun', ref: i, slots: onSide ? need : 1, priority: onSide ? 70 : 32, x: m.x, z: m.z - back, alt: 0, spread: 0.75 });
    });
    // Pumps.
    if (ship.flooding > 0.01) {
      needs.push({
        kind: 'pump', ref: 0, slots: o?.leaks ? clamp(Math.ceil(ship.flooding * 30) + 2, 4, 10) : clamp(Math.ceil(ship.flooding * 24), 2, 8),
        priority: o?.leaks ? 88 + ship.flooding * 5 : 50 + ship.flooding * 60, x: -1.2, z: 0, alt: 0, spread: 1.1,
      });
    }
    // Shot holes: those below the waterline are urgent.
    damage.holes.forEach((h, i) => {
      if (h.patched >= 1) return;
      const low = h.y < 1 ? 30 : 0;
      needs.push({ kind: 'repair', ref: i, slots: o?.leaks ? 3 : 2, priority: (o?.leaks ? 86 + low * 0.1 : 44 + low) + h.r * 20, x: h.x, z: Math.sign(h.z) * Math.max(0.3, deckHalfWidth(h.x) - 0.5), alt: 0, spread: 0.6 });
    });
    // Sail handling when canvas is being set or taken in.
    let pending = 0;
    for (const s of ship.sails.sails) if (!s.lost) pending += Math.abs(s.target - s.deploy) * s.area;
    const braceWork = Object.values(ship.sails.braceTarget).reduce((a, t, i) => a + Math.abs(t - Object.values(ship.sails.brace)[i]!), 0);
    if (pending > 20 || braceWork > 0.15) {
      ship.sails.masts.forEach((id, i) => {
        const spec = mast(id);
        // Hands at every mast (each its own ref: needs sharing a kind and ref share their slots).
        needs.push({ kind: 'sail', ref: i, slots: 3, priority: 56, x: spec.x - 1, z: 0, alt: pending > 20 ? spec.lowerTop - spec.deckY - 1.2 : 0, spread: 1.5, aloftMast: pending > 20 ? id : undefined });
      });
    }
    // Rigging repairs aloft.
    Object.values(damage.masts).forEach((md, i) => {
      if (md.rigging > 0.85 || md.lostLevel >= 3 || !ship.sails.masts.includes(md.id)) return;
      const spec = mast(md.id);
      needs.push({ kind: 'rig', ref: i, slots: o?.spars ? 3 : 2, priority: (o?.spars ? 83 : 22) + (1 - md.rigging) * 25, x: spec.x, z: 0, alt: spec.lowerTop - spec.deckY - 0.8, spread: 1, aloftMast: md.id });
    });
    // A spare spar swayed up from the deck in place of one shot away (a jury
    // mast for a lower mast), once the wreck of the old is cleared.
    if (damage.spares > 0) {
      Object.values(damage.masts).forEach((md, i) => {
        if (md.lostLevel <= 0 || md.wreck > 0) return;
        const spec = mast(md.id);
        needs.push({ kind: 'spar', ref: i, slots: o?.spars ? 6 : 4, priority: o?.spars ? 85 : ctx.battle ? 12 : 40, x: spec.x - 1.2, z: 0, alt: 0, spread: 1.5 });
      });
    }
    // Shot-through canvas: the sailmakers patch the worst first (or bend on a spare).
    const torn = ship.sails.sails.map((s, i) => ({ s, i })).filter(({ s }) => !s.lost && s.cloth.health < 0.97);
    torn.sort((a, b) => a.s.cloth.health - b.s.cloth.health);
    for (const { s, i } of torn.slice(0, o?.canvas ? 6 : 4)) {
      const spec = mast(s.mount.mast);
      const gone = 1 - s.cloth.health;
      needs.push({ kind: 'patch', ref: i, slots: o?.canvas ? 3 : ctx.battle ? 1 : 2, priority: o?.canvas ? 84 + gone * 6 : (ctx.battle ? 14 : 36) + gone * 10, x: spec.x + (s.mast === 'bowsprit' ? 4 : 1.5), z: 0, alt: 0, spread: 1.2 });
    }
    // A lookout in the main top, unless we're already in the thick of it.
    if (ship.sails.masts.includes('main')) {
      const spec = mast('main');
      needs.push({ kind: 'lookout', ref: 0, slots: 1, priority: ctx.battle ? 18 : 38, x: mastXAt(spec, spec.lowerTop) - 0.8, z: 0.8, alt: spec.lowerTop - 1 - spec.deckY, spread: 0, aloftMast: 'main' });
    }
    // Powder boys between the magazine hatch and the guns.
    if (ctx.battle) needs.push({ kind: 'powder', ref: 0, slots: 2, priority: 34, x: HATCHES[2]!.x, z: 0, alt: 0, spread: 3 });
    // The wounded are carried below.
    for (const w of this.members) {
      if (w.alive && w.onBoard && w.health < 0.4) needs.push({ kind: 'aid', ref: w.id, slots: 1, priority: 26, x: w.x, z: w.z, alt: 0, spread: 0 });
    }
    // Small arms at the rail when the enemy is close.
    if (ctx.threatSide !== 0 && ctx.threatRange < 160) {
      for (let k = 0; k < 4; k++) {
        const x = -3 + k * 3;
        // Each station its own ref: needs sharing kind and ref share their slots.
        needs.push({ kind: 'musket', ref: k, slots: 1, priority: 40, x, z: ctx.threatSide * (deckHalfWidth(x) - 0.35), alt: 0, spread: 0 });
      }
    }
    if (ctx.boarding) {
      // Boarders at the rail; all hands, and the guns and pumps go unmanned.
      const slots = ctx.allHands ? 9 : 2;
      for (let k = 0; k < 10; k++) {
        const x = -4 + k * 1.4;
        needs.push({ kind: 'board', ref: k, slots, priority: ctx.allHands ? 96 : 90, x, z: ctx.boarding * (deckHalfWidth(x) - 0.4), alt: 0, spread: ctx.allHands ? 1.1 : 0.5 });
      }
    }
    // All hands to the boarding: everything else waits (but the helm, fire,
    // and the pumps if she's filling).
    const kept = ctx.allHands && ctx.boarding ? needs.filter((n) => n.kind === 'helm' || n.kind === 'fire' || n.kind === 'board' || (n.kind === 'pump' && ship.flooding > 0.4)) : needs;
    kept.sort((a, b) => b.priority - a.priority);
    this.needs = kept;

    // Assignment: keep men on valid tasks, fill the rest greedily with a
    // little human imprecision.
    const free: CrewMember[] = [];
    const taken = new Map<string, number>();
    const key = (n: { kind: TaskKind; ref: number }) => n.kind + ':' + n.ref;
    for (const m of this.members) {
      if (!m.alive || !m.onBoard || m.health < 0.4) continue;
      const still = kept.find((n) => n.kind === m.task && n.ref === m.ref);
      const k = still ? key(still) : '';
      if (still && (taken.get(k) ?? 0) < still.slots) {
        taken.set(k, (taken.get(k) ?? 0) + 1);
        // A burning cell next to him trumps a low-priority job.
        if (still.priority < 60 && this.nearFire(m, fire)) free.push(m);
        continue;
      }
      free.push(m);
    }
    for (const n of kept) {
      let have = taken.get(key(n)) ?? 0;
      while (have < n.slots && free.length) {
        let best = -1, bs = -Infinity;
        for (let i = 0; i < free.length; i++) {
          const m = free[i]!;
          const d = Math.hypot(m.x - n.x, m.z - n.z);
          let s = -d * 0.08 + m.experience * 0.5 + r.range(0, 0.6) * (1.2 - m.experience);
          if (n.kind === 'repair' && m.role === 'carpenter') s += 2;
          if (n.kind === 'gun' && m.role === 'gunner') s += 1;
          if (n.kind === 'musket' && m.role === 'marine') s += 2;
          if (n.kind === 'powder' && m.role === 'boy') s += 3;
          if (n.kind === 'helm' && m.role === 'officer') s += 1;
          if (n.kind === 'fire' && m.courage < 0.3) s -= 1;
          if (s > bs) { bs = s; best = i; }
        }
        const m = free.splice(best, 1)[0]!;
        // Now and then a man misreads the situation and goes elsewhere.
        const mistake = r.chance(0.04 * (1.1 - m.experience) * (1.3 - this.morale));
        const target = mistake && kept.length > 1 ? r.pick(kept) : n;
        this.assign(m, target, have);
        have++;
      }
      taken.set(key(n), have);
    }
    // Work that matters more still short-handed draws men off what matters
    // less (an order to the pumps takes them from the guns), a few at a time.
    const prio = new Map<string, number>();
    for (const n of kept) prio.set(key(n), n.priority);
    let moves = 4;
    for (const n of kept) {
      if (moves <= 0) break;
      let have = taken.get(key(n)) ?? 0;
      while (have < n.slots && moves > 0) {
        let best: CrewMember | null = null, bp = n.priority - 10, bd = Infinity;
        for (const m of this.members) {
          if (!m.alive || !m.onBoard || m.health < 0.4 || m.task === 'helm') continue;
          const p = prio.get(m.task + ':' + m.ref);
          if (p === undefined || p > bp) continue;
          const d = Math.hypot(m.x - n.x, m.z - n.z);
          if (p < bp || d < bd) {
            best = m;
            bp = p;
            bd = d;
          }
        }
        if (!best) break;
        const k = best.task + ':' + best.ref;
        taken.set(k, (taken.get(k) ?? 1) - 1);
        this.assign(best, n, have);
        have++;
        moves--;
      }
      taken.set(key(n), have);
    }
    // Everyone else: take cover behind the bulwark away from the enemy in
    // action, or loaf about the waist.
    for (const m of free) {
      if (ctx.battle && ctx.threatSide !== 0) {
        if (m.task === 'cover') continue;
        const side = -ctx.threatSide;
        const x = r.range(-4, 10);
        this.assign(m, { kind: 'cover', ref: 0, slots: 1, priority: 0, x, z: side * (deckHalfWidth(x) - 0.45), alt: 0, spread: 0 });
      } else if (m.task !== 'idle' || m.taskTime > r.range(20, 60)) {
        const x = r.range(-5, 11);
        this.assign(m, { kind: 'idle', ref: 0, slots: 1, priority: 0, x, z: r.range(-1, 1) * (deckHalfWidth(x) - 0.8), alt: 0, spread: 0 });
      }
    }
  }

  /** Sailmakers' work on a sail: stitch the holes from their edges in, or with nothing left to sew to, bend on a spare. */
  private stitch(sail: SailState, work: number, ctx: CrewContext): void {
    const c = sail.cloth;
    if (c.health >= 0.999) return;
    // Two men make good a whole sail in about a minute and a half.
    sail.stitch += work * 0.0065 * c.count;
    if (sail.stitch < 1) return;
    const n = Math.floor(sail.stitch);
    const done = c.patch(n);
    if (done > 0) sail.stitch -= n;
    else if (sail.stitch > c.count * 0.6) {
      c.mend();
      sail.stitch = 0;
    }
    const h = c.health;
    sail.torn = Math.min(sail.torn, h < 0.2 ? 2 : h < 0.55 ? 1 : 0) as 0 | 1 | 2;
    if (h >= 0.999) {
      sail.stitch = 0;
      ctx.events.push({ type: 'sailMended', vessel: ctx.vessel, sail: sail.spec.name });
    }
  }

  /** The spare spar is up and rigged: the mast stands a level higher again, its canvas bent on new. */
  private sendUp(md: MastDamage, ctx: CrewContext): void {
    const lost = md.lostLevel;
    md.lostLevel--;
    md.jury = 0;
    ctx.damage.spares--;
    // A fished topmast or a jury mast: weaker than what she was built with.
    md.hp = Math.max(md.hp, md.max * (lost === 3 ? 0.45 : 0.6));
    md.rigging = Math.min(md.rigging, lost === 3 ? 0.55 : 0.7);
    if (lost === 3) md.stump = 0;
    ctx.ship.sails.restoreMast(md.id, md.lostLevel);
    ctx.damage.version++;
    ctx.events.push({ type: 'sparRigged', vessel: ctx.vessel, mast: md.id, level: lost });
  }

  private nearFire(m: CrewMember, fire: FireSystem): boolean {
    for (const c of fire.cells) if (c.level === 1 && c.heat > 0.3 && Math.hypot(c.x - m.x, c.z - m.z) < 1.8) return true;
    return false;
  }

  private assign(m: CrewMember, n: Need, slot = 0): void {
    const changed = m.task !== n.kind || m.ref !== n.ref;
    m.task = n.kind;
    m.ref = n.ref;
    const a = (slot * 2.399) % (Math.PI * 2);
    let tx = n.x + Math.cos(a) * n.spread * (slot > 0 ? 1 : 0);
    let tz = n.z + Math.sin(a) * n.spread * 0.5 * (slot > 0 ? 1 : 0);
    let alt = n.alt;
    if (n.aloftMast && alt > 0) {
      // Out along the yard or in the top.
      const spec = mast(n.aloftMast);
      tx = mastXAt(spec, spec.deckY + alt) + (n.kind === 'sail' ? 0.1 : -0.3);
      tz = (slot - 1) * 2.4;
      alt = n.kind === 'sail' ? alt + 1.3 : alt;
    }
    const hw = deckHalfWidth(tx) - 0.3;
    m.tx = tx;
    m.tz = clamp(tz, -hw, hw);
    m.tAlt = alt;
    m.hasWp = false;
    if (changed) {
      m.taskTime = 0;
      m.hesitate = Math.max(m.hesitate, this.rng.range(0.1, 1.2) * (1.25 - m.experience) * (1.4 - this.morale));
    }
  }

  private move(m: CrewMember, dt: number, ctx: CrewContext): void {
    if (m.hesitate > 0 && m.alt < 0.2) return;
    // Aloft: climb back down before going anywhere else on deck.
    const wantAlt = m.tAlt;
    const urgent = ctx.battle || m.task === 'fire' || m.task === 'abandon';
    const speed = (urgent ? 3.0 : 1.3) * (0.7 + 0.3 * m.experience) * clamp(m.health, 0.5, 1) / Math.max(0.5, ctx.ship.sx);
    // (A man going up is on his way from the shrouds at the side to his place
    // in the top, so judge him against that path, not against the place: he'd
    // otherwise be called down again the moment he left the deck.)
    const climbFrom = Math.sign(m.tz || 1) * (deckHalfWidth(m.tx) - 0.15);
    const up = Math.min(1, m.alt / Math.max(wantAlt, 0.1));
    const onPath = Math.abs(m.x - m.tx) <= 1.5 && Math.abs(m.z - (climbFrom + (m.tz - climbFrom) * up * up)) <= 1.5;
    if (m.alt > 0.2 && (!onPath || wantAlt < 0.2)) {
      m.alt = Math.max(0, m.alt - 2.2 * dt);
      m.stance = 'climb';
      if (m.alt <= 0) m.z = clamp(m.z, -(deckHalfWidth(m.x) - 0.3), deckHalfWidth(m.x) - 0.3);
      return;
    }
    let gx = m.tx, gz = m.tz;
    // Route between decks by the ladders.
    const from = levelOf(m.x), to = levelOf(m.tx);
    if (from !== to && m.alt <= 0.1) {
      const upTo = from === 'main' ? to : from;
      let best: (typeof LADDERS)[number] | null = null, bd = Infinity;
      for (const l of LADDERS) {
        if (l.up !== upTo) continue;
        const p = from === 'main' ? l.low : l.high;
        const d = Math.hypot(p.x - m.x, p.z - m.z);
        if (d < bd) { bd = d; best = l; }
      }
      if (best) {
        const entry = from === 'main' ? best.low : best.high;
        const exit = from === 'main' ? best.high : best.low;
        const onLadder = Math.abs(m.z - entry.z) < 0.35;
        if (!onLadder || Math.hypot(m.x - entry.x, m.z - entry.z) > 0.4 && Math.hypot(m.x - exit.x, m.z - exit.z) > 0.4 && !(m.x > Math.min(entry.x, exit.x) && m.x < Math.max(entry.x, exit.x))) {
          gx = entry.x;
          gz = entry.z;
        } else {
          gx = exit.x;
          gz = exit.z;
        }
      }
    }
    // Aloft tasks: go to the shrouds, then climb.
    if (wantAlt > 0.2) {
      const hw = deckHalfWidth(m.tx);
      const shroudZ = Math.sign(m.tz || 1) * (hw - 0.15);
      const atShroud = Math.abs(m.x - m.tx) < 1.2 && Math.abs(m.z - shroudZ) < 0.5;
      if (m.alt > 0.1 || atShroud) {
        m.alt = Math.min(wantAlt, m.alt + 1.4 * dt);
        m.stance = 'climb';
        // Up the shrouds toward the mast, then out onto the yard.
        const t = m.alt / Math.max(wantAlt, 0.1);
        m.x += (m.tx - m.x) * Math.min(1, dt * 1.5);
        m.z = shroudZ + (m.tz - shroudZ) * t * t;
        m.facing = Math.atan2(-m.z, 0.001);
        return;
      }
      gx = m.tx;
      gz = shroudZ;
    }
    let dx = gx - m.x, dz = gz - m.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.05) {
      if (m.stance === 'walk' || m.stance === 'run') m.stance = 'stand';
      return;
    }
    dx /= d;
    dz /= d;
    // Steer around fittings and each other.
    let ax = 0, az = 0;
    for (const o of OBSTACLES) {
      const ox = m.x - o.x, oz = m.z - o.z;
      const od = Math.hypot(ox, oz);
      if (od < o.r + 0.4 && od > 1e-3 && Math.abs(o.x - m.tx) + Math.abs(o.z - m.tz) > o.r) {
        const k = (o.r + 0.4 - od) * 2.5;
        ax += (ox / od) * k;
        az += (oz / od) * k;
      }
    }
    if (m.id % 3 === 0) {
      for (const o of this.members) {
        if (o === m || !o.alive || !o.onBoard) continue;
        const ox = m.x - o.x, oz = m.z - o.z;
        const od = ox * ox + oz * oz;
        if (od < 0.36 && od > 1e-6) {
          ax += ox * 1.5;
          az += oz * 1.5;
        }
      }
    }
    let vx = dx + ax, vz = dz + az;
    const vl = Math.hypot(vx, vz) || 1;
    vx /= vl;
    vz /= vl;
    const step = Math.min(d, speed * dt);
    m.x += vx * step;
    m.z += vz * step;
    const hw = deckHalfWidth(m.x) - 0.25;
    m.z = clamp(m.z, -hw, hw);
    m.x = clamp(m.x, -17.5, 18.4);
    m.facing += wrapAngle(Math.atan2(-vz, vx) - m.facing) * Math.min(1, dt * 8);
    m.stance = speed > 2 ? 'run' : 'walk';
    // Over the side when abandoning ship.
    if (m.task === 'abandon' && d < 0.5) this.overboard(m, ctx);
  }

  private overboard(m: CrewMember, ctx: CrewContext): void {
    const side = Math.sign(m.z) || 1;
    const p = ctx.ship.designToWorld(tmpV.set(m.x, sheerY(m.x), side * (deckHalfWidth(m.x) + 1.2)), tmpV);
    m.onBoard = false;
    m.wx = p.x;
    m.wy = p.y;
    m.wz = p.z;
    m.stance = 'swim';
    m.taskTime = 0;
  }

  private swim(m: CrewMember, dt: number, ctx: CrewContext): void {
    const h = ctx.waterHeight(m.wx, m.wz);
    if (m.wy > h + 0.1) {
      m.wy -= 6 * dt;
      return;
    }
    m.wy = h - 0.2;
    // Strike out away from the wreck.
    const c = ctx.ship.com;
    const dx = m.wx - c.x, dz = m.wz - c.z;
    const d = Math.hypot(dx, dz) || 1;
    if (d < 40) {
      m.wx += (dx / d) * 0.8 * dt;
      m.wz += (dz / d) * 0.8 * dt;
    }
    m.facing = Math.atan2(dz, dx);
    // Men in the water don't last for ever.
    if (m.taskTime > 240 && this.rng.chance(0.01 * dt)) {
      m.alive = false;
      m.stance = 'dead';
    }
  }

  /** Current task list (for debugging / HUD). */
  get plannedNeeds(): readonly Need[] {
    return this.needs;
  }
}

function workStance(t: TaskKind): Stance {
  switch (t) {
    case 'gun': case 'pump': case 'repair': case 'fire': case 'powder': case 'patch': return 'work';
    case 'sail': case 'rig': case 'spar': return 'haul';
    case 'musket': return 'aim';
    case 'cover': return 'crouch';
    case 'board': return 'fight';
    case 'surrender': return 'hands';
    case 'aid': return 'work';
    default: return 'stand';
  }
}

/**
 * How their heart shows in their work: men in high spirits serve the guns
 * and do everything else faster than steady ones, shaken men slower.
 */
export function spirit(morale: number): number {
  return 0.55 + 0.6 * clamp(morale, 0, 1);
}

const tmpV = new Vec3();
